<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import { useStore } from 'vuex'
import emitter from '@/services/emitter.service'
import SheetModal from '@/components/ui/SheetModal.vue'
import { translate } from '@/composables/useCaptionSettings'
import alertIcon from '@/assets/icons/sorenson/alert-circle.svg'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  // from the participant's session_role (host or sponsor), not the store's is_room_owner:
  // that one is only set when the room was created in this browser
  isHost: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const store = useStore()

// Permanent and temporary rooms outlive their host, so the host only leaves them.
const isRegularRoom = computed(() => {
  const roomType = store.state.event.event?.room_type
  return roomType !== 'permanent' && roomType !== 'temporary'
})

const content = computed(() => {
  if (props.isHost && isRegularRoom.value) {
    return {
      title: translate('end_session_modal_title', 'End the session?'),
      text: [translate('end_session_modal_text', 'This will end the session for all participants.')],
      confirm: translate('end_session_modal_confirm', 'End session'),
      testId: 'end'
    }
  }

  if (props.isHost) {
    return {
      title: translate('leave_permanent_session_modal_title', 'Leaving permanent session'),
      text: [
        translate('leave_permanent_session_modal_text', 'Leaving the permanent session does not end the session.'),
        translate('leave_permanent_session_modal_note', 'If audience participation is on, people can continue to use captioning/translation minutes.')
      ],
      confirm: translate('exit_session_modal_confirm', 'Exit session'),
      testId: 'leave-permanent'
    }
  }

  return {
    title: translate('exit_session_modal_title', 'Exit the session?'),
    text: [translate('exit_session_modal_text', 'Are you sure you want to leave the session?')],
    confirm: translate('exit_session_modal_confirm', 'Exit session'),
    testId: 'exit'
  }
})

const close = () => {
  emit('update:modelValue', false)
}

const confirm = () => {
  close()
  if (isRegularRoom.value) {
    emitter.emit('close_room', { is_room_owner: props.isHost })
  } else {
    emitter.emit('quit_room')
  }
}
</script>

<template>
  <sheet-modal
      :model-value="modelValue"
      @update:model-value="emit('update:modelValue', $event)"
      exact-width="w-[350px]"
      body-class="px-5 pt-[30px] pb-6"
      :has-close="false"
      :scrollable="false"
  >
    <div class="flex flex-col items-center text-center" :data-testid="`leave-session-modal-${content.testId}`">
      <img :src="alertIcon" class="size-[52px]" alt="">
      <h2 class="mt-[30px] text-white text-2xl font-bold leading-[30px]" data-testid="leave-session-modal-title">{{ content.title }}</h2>
      <div class="mt-2 text-text-neutral-high text-base leading-6" data-testid="leave-session-modal-text">
        <p v-for="(line, index) in content.text" :key="index">{{ line }}</p>
      </div>

      <div class="mt-7 w-full flex flex-col gap-2">
        <button
            type="button"
            class="w-full h-12 px-5 bg-[#6CBFFF] hover:bg-[#6CBFFF]/90 rounded-lg text-black text-base font-medium leading-normal transition duration-160 cursor-pointer"
            @click="confirm"
            data-testid="leave-session-modal-confirm"
        >
          {{ content.confirm }}
        </button>
        <button
            type="button"
            class="w-full h-12 px-5 rounded-lg text-[#6CBFFF] hover:text-[#6CBFFF]/80 text-base font-medium leading-normal transition duration-160 cursor-pointer"
            @click="close"
            data-testid="leave-session-modal-cancel"
        >
          {{ $t('common_cancel') }}
        </button>
      </div>
    </div>
  </sheet-modal>
</template>
